import { LitElement, html, TemplateResult, nothing } from 'lit';
import { property, customElement, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../utils/translator.helper';
import { AnswerType, INVALID_SHOW_MORE, renderSideMenuDescription } from '../utils';
import {
  renderLink,
  renderRiskScoreWithoutBreakdown,
  RiskManagementSideMenuEvents,
  renderSideMenuSectionTitle,
} from '../../riskManagement/sideMenus/common/common';
import { FontSizes, FontWeights } from '../../types/common/types';
import { LinkType } from '../../ui/gzLinkComponent/types';
import { BaseWebComponentTranslator } from '../../framework/BaseWebComponentTranslator';
import { buildHash } from '@nirvana/shared/navigation';
import { ColumnKeys as DevicesColumnKeys } from '../../riskManagement/devicesApp/types';
import { ScoreLevels } from '../../riskManagement/common/constants';
import _ from 'lodash';
import { repeat } from 'lit/directives/repeat.js';

/**
 * @summary Type here the description of this component
 * @tag gz-application-cve-list
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-application-cve-list')
export class ApplicationCveListWebComponent extends LitElement {
  @property({ type: Array, attribute: false })
  cveList = null as null | Array<any>;

  @property({ type: Object, attribute: false })
  searchedValue = '';

  @property({ type: String, attribute: false })
  companyId = null as null | string;

  @property({ type: Boolean, attribute: false })
  isVersionVisible = true;

  @property({ type: Boolean, attribute: false })
  hasViewDevicesButtonVisible = false;

  @state()
  isShowMorePresent = false;

  @property({
    type: Array,
    attribute: false,
    reflect: false,
  })
  _items;

  @state()
  displayedItems;

  @property({
    type: String,
    attribute: false,
  })
  showMoreButtonText = translateFn('Show more');

  @property({
    type: Number,
    attribute: false,
  })
  showMoreLimit = INVALID_SHOW_MORE;

  @property({
    type: Number,
    attribute: false,
  })
  increment = INVALID_SHOW_MORE;

  @property({ type: Boolean, attribute: false })
  isRenderSectionTitle = true;

  static get styles() {
    return styles;
  }

  render(): TemplateResult {
    if (!this.cveList) {
      return html` <gz-loader></gz-loader>`;
    }

    const filteredCVEs = this.cveList.filter(cve =>
      cve['standardId'].toLowerCase().includes(this.searchedValue.toLowerCase())
    );

    this.items = filteredCVEs;

    return html`
      <div class="header-cve">
        ${this.isRenderSectionTitle ? renderSideMenuSectionTitle(translateFn('CVE ID') + ` (${filteredCVEs.length})`) : null}
        <gz-input
          class="pb-2"
          value="${this.searchedValue}"
          placeholder=${translateFn('CVE code')}
          @change="${this.handleCveIdInputChange}"
        ></gz-input>
      </div>
      <div class="content-cve">
        ${this.renderFilteredCves(this.displayedItems)}
        ${this.isShowMorePresent
          ? html`
              <gz-link @click="${e => this.handleShowMoreClick(e)}">
                <gz-text
                  class="show-more-button"
                  fontSize="${FontSizes.PARAGRAPH_11}"
                  fontWeight="${FontWeights.NORMAL}"
                  >${this.showMoreButtonText}
                </gz-text>
              </gz-link>
            `
          : nothing}
      </div>
    `;
  }

  private renderFilteredCves(filteredCVEs: Array<any>): TemplateResult {
    if (filteredCVEs.length === 0) {
      this.isShowMorePresent = false;
      return html`
            <gz-image-repo state="empty">
                <gz-title fontWeight="${FontWeights.MEDIUM} fontSize=" ${FontSizes.HEADING_4}
                    ">${translateFn('No results found.')}</gz-title>
            </gz-image-repo>`;
    }

    return html`
      ${repeat(
        filteredCVEs,
        item => item['standardId'],
        (cve, index) => {
          const copyToClipboard = {
            enabled: true,
            text: cve['standardId'],
          };
          return html`<gz-accordion align="left" .copyToClipboard="${copyToClipboard}">
            <span
              slot="dynamic-title-content"
              class="cell-label d-flex align-items-center min-width-zero flex-1 accordion-title has-bullet ${this.getBulletClass(
                !cve.hasMissingDetails ? cve['riskScore']['value'] : null
              )}"
            >
              <gz-tooltip tooltipText="${cve['standardId']}" isEllipsis>
                <gz-text fontSize="${FontSizes.PARAGRAPH_12}" class="d-inline">${cve['standardId']}</gz-text>
              </gz-tooltip>
            </span>
            <div class="pb-1">
              ${!cve.hasMissingDetails ? this.renderCVEDetails(cve) : nothing}
              <div class="d-flex flex-row align-items-center link-container pt-1">
                <gz-tooltip tooltipText="${translateFn('View CVE database')}" isEllipsis>
                  <gz-link
                    class="text-uppercase d-inline"
                    fontSize="${FontSizes.PARAGRAPH_11}"
                    type="${LinkType.QUIET}"
                    href="${this.generateCVELink(cve['standardId'])}"
                  >
                    ${translateFn('View CVE database')}
                  </gz-link>
                </gz-tooltip>
                ${this.hasViewDevicesButtonVisible
                  ? renderLink(translateFn('View resources'), this.generateViewDevicesUrl(cve['standardId']), true)
                  : nothing}
              </div>
            </div>
          </gz-accordion>`;
        }
      )}
    `;
  }

  handleShowMoreClick(e) {
    this.isShowMorePresent = false;
    if (this.showMoreLimit === INVALID_SHOW_MORE || this.increment === INVALID_SHOW_MORE) {
      return;
    }
    this.showMoreLimit += this.increment;
    this.render();
  }

  get items(): Array<TemplateResult> {
    return this._items;
  }

  /**
   * array of categories to be rendered in a list
   */
  set items(prop: Array<TemplateResult>) {
    if (this.showMoreLimit === INVALID_SHOW_MORE) {
      this._items = prop;
      this.displayedItems = prop;
      return;
    }

    const oldItems = this._items;
    this._items = prop;
    if (this._items.length > this.showMoreLimit) {
      this.isShowMorePresent = true;
      this.displayedItems = this._items.slice(0, this.showMoreLimit);
    } else {
      this.displayedItems = _.clone(this._items);
      this.isShowMorePresent = false;
    }

    this.requestUpdate('items', oldItems);
  }

  renderCVEDetails(cve) {
    return html` ${this.isVersionVisible ? this.renderCVSSVersion(cve['riskScore']['version']) : nothing}
    ${renderRiskScoreWithoutBreakdown(cve['riskScore']['value'])}
    ${this.renderAnswerType(
      translateFn('Targets your industry'),
      cve['isIndustryTargeted'] ? AnswerType.YES : AnswerType.NO
    )}
    ${this.renderAnswerType(translateFn('Exploits'), cve['hasExploit'] ? AnswerType.YES : AnswerType.NO)}
    ${cve.summary ? renderSideMenuDescription(cve.summary) : nothing}`;
  }

  handleCveIdInputChange(event: CustomEvent): void {
    this.searchedValue = event.detail.sourceEvent;
  }

  renderAnswerType(label: string, value: AnswerType): TemplateResult {
    return html` <gz-labeled-content label="${label}">
      ${BaseWebComponentTranslator.getInstance().getRiskManagementAnswerType(value)}
    </gz-labeled-content>`;
  }

  generateCVELink(standardId: string): string {
    return `https://nvd.nist.gov/vuln/detail/${standardId}`;
  }

  private generateViewDevicesUrl(cve: string): string {
    const filters = [
      {
        [DevicesColumnKeys.DEVICE_CVES]: {
          complexEnumFilter: [
            {
              label: cve,
              value: cve,
            },
          ],
        },
      },
      {
        [DevicesColumnKeys.COMPANY]: { exactMatch: this.companyId },
      },
    ];
    return buildHash('/risk-management-resources', { filters });
  }

  private renderCVSSVersion(version: string) {
    if (!version) {
      return nothing;
    }

    return html` <gz-labeled-content label="${translateFn('Version')}"> ${'cvss ' + version}</gz-labeled-content> `;
  }

  private getBulletClass(score: number | null) {
    if (score === null) {
      return 'severity-none';
    }

    switch (true) {
      case score >= ScoreLevels.HIGH:
        return 'severity-high';
      case score >= ScoreLevels.MEDIUM && score < ScoreLevels.HIGH:
        return 'severity-medium';
      case score >= ScoreLevels.LOW && score < ScoreLevels.MEDIUM:
        return 'severity-low';
      default:
        return 'severity-compliant';
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-application-cve-list': ApplicationCveListWebComponent;
  }
}
